import React, { ReactElement } from 'react';
import { BASE_URL } from '../constants/routes';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Section } from './Section';
import { Container } from './Container';
import { Button } from './buttons/Button';
import { Colors } from '../constants/colors';
import { Device } from '../constants/device';
import { IkemTraining } from '../constants/ikemTraining';
import { TrainingProgramItem } from '../model/TrainingProgramItem';
import { ArrowRightDiagonal } from './icons/ArrowRightDiagonal';
import { usePageMeta } from '../helpers/usePageMeta';
import { localizedPath, unprefixedPath } from '../helpers/localizedPath';
import { showsIkemTraining } from '../helpers/showsIkemTraining';

const registrationFormLink = 'https://forms.gle/UDd5VjvcpKQKz4qi6';

const stripHtml = (text: string) => text.replace(/<[^>]*>/g, '');

// Search engines show dated events in their own result format, but only when
// the page states the facts in a machine-readable way. The site is prerendered,
// so the markup below ends up in the static HTML crawlers see.
const buildEventMarkup = (name: string, description: string, place: string, url: string) => {
  const offer = (price: number, validity: Record<string, string>) => ({
    '@type': 'Offer',
    url: registrationFormLink,
    availability: 'https://schema.org/InStock',
    price,
    priceCurrency: IkemTraining.currency,
    priceSpecification: {
      '@type': 'PriceSpecification',
      price,
      priceCurrency: IkemTraining.currency,
      valueAddedTaxIncluded: false,
    },
    ...validity,
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'EducationEvent',
    name,
    description,
    url,
    startDate: IkemTraining.start,
    endDate: IkemTraining.end,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    // The training is held in Czech whichever version of the site announces it.
    inLanguage: 'cs',
    location: {
      '@type': 'Place',
      name: place,
      address: {
        '@type': 'PostalAddress',
        ...IkemTraining.venue,
      },
    },
    organizer: {
      '@type': 'Organization',
      name: 'slp.blue',
      url: 'https://slp.blue',
    },
    offers: [offer(IkemTraining.price, { validThrough: IkemTraining.registrationEnds })],
  };
};

/**
 * Standalone page of the IKEM training, linked from the banner on the home page.
 * Once the training is over the page stops existing and sends visitors home, so
 * a link shared by e-mail never lands on a past event.
 */
export const Training = (): ReactElement => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();

  if (!showsIkemTraining(i18n.language)) {
    return <Navigate to={localizedPath(pathname, '/')} replace />;
  }

  return <TrainingPage />;
};

const TrainingPage = (): ReactElement => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const topics = t('training.topics', { returnObjects: true }) as string[];
  const program = t('training.program', { returnObjects: true }) as TrainingProgramItem[];

  const canonical = `${BASE_URL}${unprefixedPath(pathname)}`;
  const title = t('training.meta.title');
  const description = t('training.meta.description');

  usePageMeta({
    title,
    description,
    canonical,
    ogTitle: title,
    ogDescription: description,
    ogUrl: canonical,
    jsonLd: buildEventMarkup(t('section.training'), stripHtml(t('training.intro')), t('training.whenAndWhere.place'), canonical),
  });

  const handleOpenRegistrationForm = () => {
    window.open(registrationFormLink, '_blank', 'noopener noreferrer');
  };

  const registrationButton = (inverse?: boolean) => (
    <RegistrationButtonContainer>
      <Button onClick={handleOpenRegistrationForm} inverse={inverse} maxWidth={'420px'}>
        {t('training.registrationButton')}
        <ArrowRightDiagonal color={inverse ? Colors.primary : Colors.white} />
      </Button>
    </RegistrationButtonContainer>
  );

  return (
    <main>
      <Hero>
        <Container compact>
          <h1>{t('section.training')}</h1>
          <DividingLine />
          <HeroLabel>{t('training.whenAndWhere.title')}</HeroLabel>
          <HeroFacts>
            <strong>{t('training.whenAndWhere.date')}</strong>
            <span>{t('training.whenAndWhere.place')}</span>
          </HeroFacts>
          {registrationButton(true)}
        </Container>
      </Hero>

      <Section id={'training-intro'} inverse heading={t('training.aboutTitle')}>
        <Container compact>
          <TrainingContent>
            <Perex dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t('training.intro')) }} />

            <Block>
              <h3>{t('training.topicsTitle')}</h3>
              <ul>
                {topics.map((topic, key) => (
                  <li key={key}>{topic}</li>
                ))}
              </ul>
            </Block>

            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t('training.ownTopic')) }} />
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t('training.consultant')) }} />
          </TrainingContent>
        </Container>
      </Section>

      <Section id={'training-program'} heading={t('training.programTitle')}>
        <Container compact>
          <TrainingContent>
            <Program>
              {program.map((item, key) => (
                <ProgramItem key={key}>
                  <ProgramTime>{item.time}</ProgramTime>
                  <ProgramTopic>
                    <span>{item.title}</span>
                    {item.speaker && <ProgramSpeaker>{item.speaker}</ProgramSpeaker>}
                  </ProgramTopic>
                </ProgramItem>
              ))}
            </Program>
            <Note>{t('training.programNote')}</Note>
            <p>{t('training.certificate')}</p>
          </TrainingContent>
        </Container>
      </Section>

      <Section id={'training-registration'} inverse heading={t('training.registrationTitle')}>
        <Container compact>
          <TrainingContent>
            <Block>
              <h3>{t('training.fee.title')}</h3>
              <p>{t('training.fee.price')}</p>
            </Block>
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t('training.ownComputer')) }} />
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t('training.registrationDeadline')) }} />
            {registrationButton()}
          </TrainingContent>
        </Container>
      </Section>
    </main>
  );
};

const Hero = styled.div`
  background: ${Colors.primary};
  color: ${Colors.white};
  padding: 80px 0 90px;

  ${Device.tablet} {
    padding: 40px 0 50px;
  }

  ${Container} {
    align-items: flex-start;
  }

  h1 {
    font-size: 42px;
    font-weight: 900;
    margin: 0 0 10px;
    max-width: 900px;

    ${Device.tablet} {
      font-size: 28px;
    }
  }
`;

const DividingLine = styled.div`
  background: ${Colors.primaryLight};
  height: 5px;
  width: 100px;
  margin-bottom: 25px;
`;

const HeroLabel = styled.p`
  margin: 0 0 5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 14px;
  color: ${Colors.primaryLight};
`;

const HeroFacts = styled.p`
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 22px;
  line-height: 1.6;
  margin: 0;

  ${Device.tablet} {
    font-size: 18px;
  }
`;

const TrainingContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  p {
    text-align: left;
    margin: 0;
  }

  a {
    color: ${Colors.primary};
  }

  ul {
    margin: 0;
    padding-left: 25px;
    line-height: 2;
  }
`;

const Perex = styled.p`
  font-size: 18px;

  ${Device.tablet} {
    font-size: 16px;
  }
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  h3 {
    color: ${Colors.primary};
    margin: 0;
  }
`;

const Program = styled.div`
  display: flex;
  flex-direction: column;
  background: ${Colors.background};
  border-radius: 20px;
  padding: 20px 30px;

  ${Device.tablet} {
    padding: 15px 20px;
  }
`;

const ProgramItem = styled.div`
  display: flex;
  flex-direction: row;
  gap: 30px;
  padding: 10px 0;
  line-height: 1.5;

  & + & {
    border-top: 1px solid ${Colors.primaryLight};
  }

  ${Device.tablet} {
    flex-direction: column;
    gap: 5px;
  }
`;

const ProgramTime = styled.span`
  font-weight: 600;
  color: ${Colors.primary};
  white-space: nowrap;
  min-width: 120px;
`;

const ProgramTopic = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProgramSpeaker = styled.span`
  color: ${Colors.darkGrey};
  font-size: 14px;
`;

const Note = styled.p`
  color: ${Colors.darkGrey};
  font-size: 14px;
`;

const RegistrationButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;
  width: 100%;

  button {
    width: auto;
  }

  ${Hero} & {
    justify-content: flex-start;
    margin-top: 35px;
  }
`;
